"use client";

import React, { useCallback, useMemo } from "react";

import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  type Selection,
} from "@nextui-org/react";
import { MoreVertical } from "lucide-react";
import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import { useUserRole } from "@/hooks/useUserRole";
import ArchiveIcon from "@/icons/ui/buttons/archive-box.svg";
import EditIcon from "@/icons/ui/buttons/edit.svg";
import RefreshIcon from "@/icons/ui/buttons/refresh.svg";
import ShareIcon from "@/icons/ui/buttons/share.svg";
import TrashIcon from "@/icons/ui/buttons/trash.svg";
import UserIcon from "@/icons/ui/buttons/user.svg";
import { cn } from "@/utils/cn";

import { useContextMenuActions } from "./hooks/useContextMenuActions";

export interface UniversalContextMenuProps {
  /** Property ID for actions */
  propertyId: string;
  /** Property data for context */
  propertyData?: {
    title?: string;
    url?: string;
    agent?: {
      name?: string;
      phone?: string;
    };
  };
  /** Whether menu is disabled */
  disabled?: boolean;
  /** CSS class name for custom styling */
  className?: string;
  /** CSS class name for trigger button */
  triggerClassName?: string;
  /** Menu placement */
  placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
  /** Custom trigger element */
  customTrigger?: React.ReactNode;
  /** Show advanced actions based on context */
  showAdvancedActions?: boolean;
  /** Integration context (table, card, etc.) */
  context?: "table" | "card" | "bulk";
  /** Action callbacks */
  onEdit?: () => void;
  onArchive?: () => void;
  onPromote?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  onViewStats?: () => void;
  onActualize?: () => void;
}

/**
 * UniversalContextMenu - Extended RealtorContextMenu for table and card views
 *
 * Features:
 * - Role-based action visibility (USER_REALTOR, ADMIN permissions)
 * - Property-specific actions: Актуализировать, Удалить, Отправить в архив, Редактировать, Поделиться
 * - Agent information display (name + phone)
 * - Property URL display
 * - Integration with bulkActions.service.ts
 * - Context-aware action sets (table vs card)
 *
 * UI Specification:
 * - Extends existing RealtorContextMenu design
 * - Additional sections for agent info and property details
 * - Role-based section visibility
 *
 * @example
 * ```tsx
 * <UniversalContextMenu
 *   propertyId="123"
 *   context="table"
 *   propertyData={{
 *     title: "Beautiful Apartment",
 *     url: "https://avezor.com/properties/123",
 *     agent: { name: "John Doe", phone: "+1234567890" }
 *   }}
 *   showAdvancedActions={true}
 *   onActualize={() => handleActualize('123')}
 *   onDelete={() => handleDelete('123')}
 * />
 * ```
 */
export const UniversalContextMenu: React.FC<UniversalContextMenuProps> = ({
  propertyId,
  propertyData,
  disabled = false,
  className,
  triggerClassName,
  placement = "bottom-end",
  customTrigger,
  showAdvancedActions = false,
  context = "card",
  onEdit,
  onArchive,
  onPromote,
  onDuplicate,
  onDelete,
  onShare,
  onViewStats,
  onActualize,
}) => {
  const t = useTranslations();
  const { userRole, isRealtor, isAdmin, hasEnhancedAccess } = useUserRole();
  const {
    actualizeProperty,
    duplicateProperty,
    archiveProperty,
    deleteProperty,
    shareProperty,
    isLoading,
    isActualizing,
    isDuplicating,
    isArchiving,
    isDeleting,
  } = useContextMenuActions();

  // Determine which actions to show based on role and context
  const visibleActions = useMemo(() => {
    const baseActions = {
      actualize: hasEnhancedAccess,
      duplicate: hasEnhancedAccess,
      archive: isRealtor || isAdmin,
      edit: hasEnhancedAccess,
      share: true, // Available for all users
    };

    const advancedActions = {
      delete: isAdmin, // Only admins can delete
      viewStats: hasEnhancedAccess,
    };

    return {
      ...baseActions,
      ...(showAdvancedActions ? advancedActions : {}),
    };
  }, [hasEnhancedAccess, isRealtor, isAdmin, showAdvancedActions]);

  // Handle menu actions with role checking and analytics
  const handleMenuAction = useCallback(
    async (key: Selection) => {
      const actionKey = Array.from(key)[0] as string;

      // Execute action handlers based on permissions
      switch (actionKey) {
        case "actualize":
          if (visibleActions.actualize) {
            await actualizeProperty.mutateAsync(propertyId);
            onActualize?.();
          }
          break;
        case "edit":
          if (visibleActions.edit) {
            onEdit?.();
          }
          break;
        case "duplicate":
          if (visibleActions.duplicate) {
            await duplicateProperty.mutateAsync(propertyId);
            onDuplicate?.();
          }
          break;
        case "archive":
          if (visibleActions.archive) {
            // Show confirmation toast for archive action
            toast(
              (toastId) => (
                <div className="flex flex-col gap-2">
                  <div className="font-medium">Archive this property?</div>
                  <div className="text-sm text-gray-600">
                    {t("contextMenu.archivePrompt") ||
                      "This action will move the property to archive."}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                      onClick={async () => {
                        toast.dismiss(toastId);
                        try {
                          const comment = "Archived via context menu";
                          await archiveProperty.mutateAsync({
                            propertyId,
                            comment,
                          });
                          toast.success("Property archived successfully");
                          onArchive?.();
                        } catch (error) {
                          toast.error("Failed to archive property");
                        }
                      }}
                    >
                      Archive
                    </button>
                    <button
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                      onClick={() => toast.dismiss(toastId)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 8000,
                position: "top-center",
              },
            );
          }
          break;
        case "share":
          if (visibleActions.share) {
            await shareProperty.mutateAsync(propertyId);
            onShare?.();
          }
          break;
        case "viewStats":
          if (visibleActions.viewStats) {
            onViewStats?.();
          }
          break;
        case "delete":
          if (visibleActions.delete) {
            // Show confirmation toast for delete action
            toast(
              (toastId) => (
                <div className="flex flex-col gap-2">
                  <div className="font-medium text-red-600">
                    Delete this property permanently?
                  </div>
                  <div className="text-sm text-gray-600">
                    {t("contextMenu.deleteConfirm") ||
                      "This action cannot be undone."}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                      onClick={async () => {
                        toast.dismiss(toastId);
                        try {
                          await deleteProperty.mutateAsync(propertyId);
                          toast.success("Property deleted successfully");
                          onDelete?.();
                        } catch (error) {
                          toast.error("Failed to delete property");
                        }
                      }}
                    >
                      Delete
                    </button>
                    <button
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                      onClick={() => toast.dismiss(toastId)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 8000,
                position: "top-center",
              },
            );
          }
          break;
        case "copyUrl":
          if (propertyData?.url) {
            try {
              await navigator.clipboard.writeText(propertyData.url);
              toast.success("Property URL copied to clipboard");
            } catch (error) {
              toast.error("Failed to copy URL to clipboard");
            }
          }
          break;
        default:
          console.warn(`Unknown action: ${actionKey}`);
      }
    },
    [
      propertyId,
      propertyData,
      visibleActions,
      actualizeProperty,
      duplicateProperty,
      archiveProperty,
      deleteProperty,
      shareProperty,
      onEdit,
      onArchive,
      onDuplicate,
      onDelete,
      onShare,
      onViewStats,
      onActualize,
      t,
    ],
  );

  // Default trigger button - use NextUI Button to ensure Dropdown integration
  const DefaultTrigger = () => (
    <Button
      isIconOnly
      radius="full"
      variant="light"
      className={cn(
        "w-10 h-10 min-w-10 min-h-10",
        "bg-white hover:bg-gray-50",
        "border border-gray-200",
        "hover:shadow-md focus-visible:shadow-[0_0_0_3px_rgba(17,86,250,0.06)]",
        "rounded-full",
        "transition-all duration-200",
        "active:scale-95",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        triggerClassName,
      )}
      aria-label={t("contextMenu.triggerLabel")}
      data-testid="universal-context-menu-trigger"
      isDisabled={disabled}
    >
      <MoreVertical
        className="w-4 h-4 text-gray-600"
        aria-hidden="true"
      />
    </Button>
  );

  return (
    <div className={cn("inline-flex", className)}>
      <Dropdown
        placement={placement}
        offset={8}
        classNames={{
          base: "before:bg-default-200",
          content: cn(
            "min-w-[240px] p-0 rounded-[25px] bg-white border border-[#d7d7d7]/35",
            "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]",
            "z-[1300]",
          ),
        }}
        // Force portal to body to avoid clipping by parent overflow/transform
        portalContainer={
          typeof window !== "undefined" ? document.body : undefined
        }
        onOpenChange={(open) => {
          if (process.env.NODE_ENV === "development") {
            console.log("UniversalContextMenu open:", open);
          }
        }}
        isDisabled={disabled || isLoading}
      >
        <DropdownTrigger>{customTrigger || <DefaultTrigger />}</DropdownTrigger>

        <DropdownMenu
          aria-label={t("contextMenu.menuLabel")}
          onAction={(key) => handleMenuAction(new Set([key]))}
          disabledKeys={disabled || isLoading ? ["all"] : []}
          itemClasses={{
            base: cn(
              // Адаптивные отступы и минимальная высота
              "rounded-none px-5 py-3 gap-2 min-h-[40px]",
              // Hover эффекты
              "data-[hover=true]:bg-gray-50",
              "data-[pressed=true]:bg-gray-100",
              "data-[focus=true]:bg-gray-50",
              "data-[disabled=true]:text-gray-400 data-[disabled=true]:cursor-not-allowed",
              "transition-colors duration-150",
            ),
            title: cn(
              // Типография Geometria 14px с правильным line-height
              "text-sm font-normal leading-[1.258] text-[#323232]",
              // Специальный стиль для актуализации
              "data-[key=actualize]:text-[#CF1409] data-[key=actualize]:font-bold",
            ),
            selectedIcon: "hidden",
          }}
        >
          {/* Property Information Section */}
          {propertyData && (
            <>
              {propertyData.title ? (
                <DropdownItem
                  key="propertyInfo"
                  className="text-xs text-gray-500 cursor-default hover:bg-transparent"
                  isReadOnly
                >
                  <div className="flex items-center gap-2">
                    <RefreshIcon className="w-3 h-3" />
                    <span className="truncate max-w-[180px]">
                      {propertyData.title}
                    </span>
                  </div>
                </DropdownItem>
              ) : null}

              {propertyData.agent ? (
                <DropdownItem
                  key="agentInfo"
                  className="text-xs text-gray-500 cursor-default hover:bg-transparent"
                  isReadOnly
                >
                  <div className="space-y-1">
                    {propertyData.agent.name ? (
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-3 h-3" />
                        <span>{propertyData.agent.name}</span>
                      </div>
                    ) : null}
                    {propertyData.agent.phone ? (
                      <div className="flex items-center gap-2">
                        <UserIcon
                          className="w-3 h-3"
                          size={12}
                        />
                        <span>{propertyData.agent.phone}</span>
                      </div>
                    ) : null}
                  </div>
                </DropdownItem>
              ) : null}

              <Divider className="my-1" />
            </>
          )}

          {/* Primary Actions */}
          {visibleActions.actualize ? (
            <DropdownItem
              key="actualize"
              startContent={<RefreshIcon className="w-4 h-4" />}
              isDisabled={isActualizing}
            >
              {t("contextMenu.actualize")}
              {isActualizing && " (обновление...)"}
            </DropdownItem>
          ) : null}

          {visibleActions.edit ? (
            <DropdownItem
              key="edit"
              startContent={<EditIcon className="w-4 h-4" />}
            >
              {t("contextMenu.edit")}
            </DropdownItem>
          ) : null}

          <Divider className="my-1" />

          {/* Secondary Actions */}
          {visibleActions.duplicate ? (
            <DropdownItem
              key="duplicate"
              startContent={<EditIcon className="w-4 h-4" />}
              isDisabled={isDuplicating}
            >
              {t("contextMenu.duplicate")}
              {isDuplicating && " (создание...)"}
            </DropdownItem>
          ) : null}

          {visibleActions.archive ? (
            <DropdownItem
              key="archive"
              startContent={<ArchiveIcon className="w-4 h-4" />}
              isDisabled={isArchiving}
            >
              {t("contextMenu.archive")}
              {isArchiving && " (архивирование...)"}
            </DropdownItem>
          ) : null}

          {visibleActions.share ? (
            <DropdownItem
              key="share"
              startContent={<ShareIcon className="w-4 h-4" />}
            >
              {t("contextMenu.share")}
            </DropdownItem>
          ) : null}

          {/* Property URL Actions */}
          {propertyData?.url ? (
            <>
              <Divider className="my-1" />
              <DropdownItem
                key="copyUrl"
                startContent={<ShareIcon className="w-4 h-4" />}
              >
                {t("contextMenu.copyUrl")}
              </DropdownItem>
            </>
          ) : null}

          {/* Advanced Actions */}
          {showAdvancedActions ? (
            <>
              <Divider className="my-1" />

              {visibleActions.viewStats ? (
                <DropdownItem
                  key="viewStats"
                  startContent={<EditIcon className="w-4 h-4" />}
                >
                  {t("contextMenu.viewStats")}
                </DropdownItem>
              ) : null}

              {visibleActions.delete ? (
                <DropdownItem
                  key="delete"
                  className="text-danger data-[hover=true]:bg-red-50"
                  color="danger"
                  startContent={<TrashIcon className="w-4 h-4" />}
                  isDisabled={isDeleting}
                >
                  {t("contextMenu.delete")}
                  {isDeleting && " (удаление...)"}
                </DropdownItem>
              ) : null}
            </>
          ) : null}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};
